import {
  diffList,
  type LogChange,
  type LogDetail,
} from "@lift-admin/utils/logChangeFormatter";

type NamedEntity = {
  id?: any;
  name?: string;
  brands?: NamedEntity[];
};

export type AccessMode = "INCLUDE" | "EXCLUDE" | "NONE";

export type AccessLogOptions = {
  /** API/UI mode when the signed ID list is empty (cannot be inferred). */
  previousMode?: string | null;
  nextMode?: string | null;
};

/** Flatten org→brands nesting (allFields) and plain entity lists into id/name pairs. */
export function flattenNamedEntities(
  entities: NamedEntity[] = []
): Array<{ id: number; name: string }> {
  const out: Array<{ id: number; name: string }> = [];
  const seen = new Set<number>();

  const push = (id: any, name?: string) => {
    const num = Number(id);
    if (!Number.isFinite(num) || seen.has(num)) return;
    seen.add(num);
    out.push({ id: num, name: (name && String(name).trim()) || `ID ${num}` });
  };

  for (const entity of entities || []) {
    if (!entity) continue;
    if (entity.id != null) push(entity.id, entity.name);
    if (Array.isArray(entity.brands)) {
      for (const brand of entity.brands) {
        if (brand?.id != null) push(brand.id, brand.name);
      }
    }
  }

  return out;
}

/** Infer access mode from signed IDs (negative = exclude, positive = include). */
export function inferAccessMode(signedIds: any[] = []): AccessMode {
  const ids = (signedIds || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id !== 0);
  if (!ids.length) return "NONE";
  if (ids.some((id) => id < 0)) return "EXCLUDE";
  return "INCLUDE";
}

function normalizeModeHint(hint?: string | null): AccessMode | null {
  if (!hint) return null;
  const upper = String(hint).toUpperCase();
  if (upper === "EXCLUDE" || upper === "INCLUDE") return upper;
  return null;
}

/** Resolve effective mode from signed IDs, falling back to an explicit hint. */
export function resolveAccessMode(
  signedIds: any[] = [],
  modeHint?: string | null
): AccessMode {
  const inferred = inferAccessMode(signedIds);
  if (inferred !== "NONE") return inferred;
  return normalizeModeHint(modeHint) || "NONE";
}

function uniqueAbsIds(signedIds: any[] = []): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const raw of signedIds || []) {
    const num = Number(raw);
    if (!Number.isFinite(num) || num === 0) continue;
    const abs = Math.abs(num);
    if (seen.has(abs)) continue;
    seen.add(abs);
    out.push(abs);
  }
  return out;
}

export function resolveEntityName(
  id: any,
  entities: NamedEntity[] = [],
  fallbackLabel = "ID"
): string {
  const absId = Math.abs(Number(id));
  if (!Number.isFinite(absId)) return String(id ?? "");
  return (
    flattenNamedEntities(entities).find((e) => e.id === absId)?.name ||
    `${fallbackLabel} ${absId}`
  );
}

/** Mode label only (never includes member names). Matches the Access Mode UI radios. */
export function formatAccessModeLabel(
  mode: AccessMode,
  kind: "Brand" | "Organization"
): string {
  const plural = kind === "Brand" ? "brands" : "organizations";
  if (mode === "EXCLUDE") return `Exclude specific ${plural}`;
  if (mode === "INCLUDE") return `Include only specific ${plural}`;
  return `${kind} Access`;
}

/**
 * One-line access summary for create/delete details.
 * Examples:
 *  - "Exclude specific brands (all brands included)"
 *  - "Include only specific brands: XCJ"
 */
export function formatAccessSummary(
  kind: "Brand" | "Organization",
  signedIds: any[] = [],
  entities: NamedEntity[] = [],
  modeHint?: string | null
): string {
  const plural = kind === "Brand" ? "brands" : "organizations";
  const mode = resolveAccessMode(signedIds, modeHint);
  const names = uniqueAbsIds(signedIds).map((id) =>
    resolveEntityName(id, entities, kind)
  );
  const modeLabel = formatAccessModeLabel(mode, kind);

  if (mode === "EXCLUDE") {
    return names.length
      ? `${modeLabel}: ${names.join(", ")}`
      : `${modeLabel} (all ${plural} included)`;
  }

  if (mode === "INCLUDE") {
    return names.length
      ? `${modeLabel}: ${names.join(", ")}`
      : `${modeLabel} (no ${plural} included)`;
  }

  return `No ${kind.toLowerCase()} access rules`;
}

/**
 * Resolve signed access IDs to readable labels.
 * Prefer formatAccessSummary / buildAccessLogChanges for activity logs.
 */
export function resolveAccessEntryLabel(
  signedId: any,
  entities: NamedEntity[] = [],
  fallbackLabel = "ID"
): string {
  const num = Number(signedId);
  if (!Number.isFinite(num)) return String(signedId ?? "");
  const name = resolveEntityName(num, entities, fallbackLabel);
  if (num < 0) return `Exclude ${name}`;
  if (num > 0) return `Include ${name}`;
  return name;
}

/**
 * Access updates:
 * - Same mode (e.g. still Exclude): list +/- only — mode label is not struck through
 * - Mode flip (Exclude ↔ Include): standard from → to like other field updates
 */
export function buildAccessLogChanges(
  kind: "Brand" | "Organization",
  previousIds: any[] = [],
  nextIds: any[] = [],
  entities: NamedEntity[] = [],
  options?: AccessLogOptions
): LogChange[] {
  const prevMode = resolveAccessMode(previousIds, options?.previousMode);
  const nextMode = resolveAccessMode(nextIds, options?.nextMode);

  const { added, removed } = diffList(
    uniqueAbsIds(previousIds),
    uniqueAbsIds(nextIds),
    {
      resolve: (id) => resolveEntityName(id, entities, kind),
    }
  );

  // Mode unchanged: show only what was added/removed under the mode label
  // (e.g. "Exclude specific brands" + VitaHustle) — no strikethrough on Exclude.
  if (prevMode === nextMode) {
    if (!added.length && !removed.length) return [];
    return [
      {
        label: formatAccessModeLabel(
          nextMode !== "NONE" ? nextMode : prevMode,
          kind
        ),
        added,
        removed,
      },
    ];
  }

  // Mode changed: full from → to summary
  const from = formatAccessSummary(
    kind,
    previousIds,
    entities,
    options?.previousMode
  );
  const to = formatAccessSummary(kind, nextIds, entities, options?.nextMode);
  if (from === to) return [];
  return [{ label: `${kind} Access`, from, to }];
}

/** Create/delete detail row for access (mode + members in one line). */
export function buildAccessLogDetails(
  kind: "Brand" | "Organization",
  signedIds: any[] = [],
  entities: NamedEntity[] = [],
  modeHint?: string | null
): LogDetail[] {
  const mode = resolveAccessMode(signedIds, modeHint);
  if (mode === "NONE" && !uniqueAbsIds(signedIds).length) return [];

  return [
    {
      label: `${kind} Access`,
      value: formatAccessSummary(kind, signedIds, entities, modeHint),
    },
  ];
}

/**
 * Infer next mode the same way save handlers do when the signed list is empty.
 */
export function inferNextAccessModeFromSignedIds(
  signedIds: any[] = [],
  fallbackMode: string | null = "INCLUDE"
): AccessMode {
  const excluded = (signedIds || []).some((id) => Number(id) < 0);
  const included = (signedIds || []).some((id) => Number(id) > 0);
  if (excluded) return "EXCLUDE";
  if (included) return "INCLUDE";
  return normalizeModeHint(fallbackMode) || "INCLUDE";
}

/**
 * @deprecated Use buildAccessLogChanges — kept for any remaining call sites.
 */
export function buildAccessListChange(
  label: string,
  previousIds: any[] = [],
  nextIds: any[] = [],
  entities: NamedEntity[] = [],
  fallbackLabel = "ID"
): { label: string; added: string[]; removed: string[] } | null {
  const kind: "Brand" | "Organization" =
    fallbackLabel === "Organization" || label.toLowerCase().includes("org")
      ? "Organization"
      : "Brand";
  const row = buildAccessLogChanges(kind, previousIds, nextIds, entities)[0];
  if (!row) return null;
  if (row.added || row.removed) {
    return {
      label: row.label,
      added: row.added || [],
      removed: row.removed || [],
    };
  }
  return {
    label: row.label,
    added: row.to ? [row.to] : [],
    removed: row.from ? [row.from] : [],
  };
}
